import Phaser from 'phaser';
import { clothTweaks } from './ClothTweaks.js';
import { ClothTweakHud } from './ClothTweakHud.js';
import { idleTracker } from '../state/idleTracker.js';
import { preloader } from '../scenes/PreloadScene.js';
import { ASSETS } from '../assetManifest.mjs';
import { ANIM_ENABLED, WALK, dirFromVector, dir8FromVector, rowFlipFromDir8, animKey, restFrame, ensureWalkSheets, ensureHairSheets, ensurePoseSheets } from './walkAnim.js';
import { POSE, POSE_SCALE, STANDING_HEAD_TOP, headTop, poseKeys, poseLayers, sleepShown } from './poseLayers.mjs';
import { hairKeys, hairFallback, itemLift, ITEM_FIT, cutSheet } from '../look/hairArt.mjs';
import { HAIR_SHEETS } from '../look/hairSheets.mjs';
import { zOrderFor, ALL_LAYERS, sheetOnly, underTheHair } from './clothSlots.mjs';
import { ownFrame } from './rightRows.mjs';
import { stepShift } from './walkShift.mjs';
import { applyLook, ensureMasks } from '../look/applyLook.js';
import { isDefaultLook } from '../look/lookCatalog.mjs';


const NATALKA_W = 160;
const NATALKA_H = 240;
export const NATALKA_SIZE = { w: NATALKA_W, h: NATALKA_H };

const CATEGORY_TO_LAYER = {
  bluzka:     'top',
  spodniczka: 'bottom',
  buty:       'shoes',
  akcesorium: 'accessory',
  kurtka:     'jacket',      // Ken's jacket / hoodie, over the tee (2026-10-01)
};

// 'accessory_back' draws BEFORE base → behind the head. An accessory with a `cloth_<id>_back`
// texture (e.g. a crown's rim) renders that part here so the head sits inside the item.


// Layers that mirror when the character faces left. 'accessory_back' is deliberately
// EXCLUDED — it is authored to sit behind the head and flipping it breaks the fit.
// Shared by RemoteAvatar (peer facing) and AltanaSeats (seat facing).
export const FLIP_LAYERS = ['base', 'shoes', 'bottom', 'top', 'jacket', 'accessory'];

// Seated look: the avatar shrinks and only the TOP fraction of every layer is drawn, so a
// seated character shows head + chest — no belly, no legs (there is no seated pose in the
// art, so we crop the standing one).
// GEOMETRY FOR CALLERS: layers are centred at (0,0) inside the container, so with
// SEAT_CROP_FRAC = 0.5 the kept top half spans -H*s/2 .. 0 — the visible BOTTOM of a seated
// avatar is exactly the container's `y`. Put the container centre on the seat's sit line.
export const SEAT_SCALE = 0.75;
export const SEAT_CROP_FRAC = 0.5;

export class NatalkaSprite extends Phaser.GameObjects.Container {
  // options.tweakSource: object with get(clothKey) -> {scaleW,scaleH,offsetX,offsetY};
  //   defaults to the local player's clothTweaks. Remote avatars pass the PEER's tweaks
  //   so other players render with their OWN garment scale/offset.
  // options.noTweakHud: skip creating the 👗 editor (remote avatars must not own it, and a
  //   visiting player can't edit an outfit in someone else's house). This says nothing about
  //   whether the sprite is YOU — see options.local — and nothing about the walk animation.
  // options.local: this sprite is the player at the keyboard, so it remembers which way it
  //   faces across scene changes. Defaults to "has a wardrobe button", which is right
  //   everywhere except the visit scenes, where you own the avatar but not the wardrobe.
  // options.worldScale: how big this character is in the SCENE's world (1 everywhere
  //   indoors; the outdoor driving scenes pass OUTDOOR_CHARACTER_SCALE so a 1.2 m child is
  //   120 px tall next to a 154 px car). It multiplies with the seated scale, and the
  //   container keeps scaling about its own centre — so a scene that shrinks the character
  //   has to move its floor/spawn y down by (240 - 240·scale) / 2 to keep her feet on the
  //   same line. Everything hung off the container (the Zzz letters, the cloth layers)
  //   scales with it for free; a label or bubble the SCENE draws must scale its own offset.
  constructor(scene, x, y, options = {}) {
    super(scene, x, y);
    this.layers = {};
    this._seated = false;
    this._worldScale = options.worldScale ?? 1;
    this._lastEquipped = null;
    this._tweakSource = options.tweakSource ?? clothTweaks;
    // Which character's body to draw — explicit option (remote peers), else the local
    // player's chosen character, else Natalka. Drives the `<character>_base` sprite.
    this._character = options.character
      ?? (typeof window !== 'undefined' ? window.__myCharacter : null) ?? 'natalka';
    const baseKey = `${this._character}_base`;
    for (const key of zOrderFor(this._character)) {
      const tex = (key === 'base')
        ? (scene.textures.exists(baseKey) ? baseKey
           : scene.textures.exists('natalka_base') ? 'natalka_base'
           : scene.textures.exists('natalka') ? 'natalka' : '__none')
        : (scene.textures.exists('natalka') ? 'natalka' : '__none');
      const sp = scene.add.image(0, 0, tex).setDisplaySize(NATALKA_W, NATALKA_H);
      if (key !== 'base') sp.setVisible(false);
      this.layers[key] = sp;
      this.add(sp);
    }
    // The body is tier 0 of every scene, but its download can still fail (a deploy
    // restarting the server mid-load): the loader retries it — draw it when it lands.
    if (!scene.textures.exists(baseKey)) {
      preloader.whenReady(baseKey, () => { if (this.active) this.layers.base.setTexture(baseKey).setDisplaySize(NATALKA_W, NATALKA_H); });
    }
    this.setScale(this._worldScale);
    scene.add.existing(this);

    // Animated 8-direction walk (walkAnim.js) — on for every sprite that can be seen walking,
    // yours and other players'. Pass `walkAnim: false` for one that never walks (the licence
    // photo, the hidden driver in a lesson) to keep the flat art and skip the sheet download.
    // `?anim=0` is the kill switch. A character with no sheet falls back to the flat art.
    this._anim = null;             // { body, bodyKey, garments: {layer: sprite} } once the body sheet is in
    this._animMissing = new Set(); // sheet keys that failed to load — never asked for twice
    this._motion = { dx: 0, dy: 0, row: 0, flip: false, dir8: 4 };
    this._inPlace = false;         // a display figure walking on the spot (walkInPlace)
    this._held = null;             // ... or one walk frame of the cycle held still (holdWalkFrame)
    this._isLocal = options.local ?? !options.noTweakHud;
    // The player's look (skin / eyes / hair, look/): a remote peer's comes with its outfit, yours from /me.
    this._look = options.look ?? (this._isLocal && typeof window !== 'undefined' ? window.__myLook : null);
    if (options.walkAnim ?? ANIM_ENABLED) {
      // Face the way we faced before the reload / scene change (BootScene passes it from the DB).
      // Face the way we faced before the reload (BootScene passes it from the DB) or before
      // the scene change (walking up-right through the front door = facing up-right inside).
      const spawnFacing = scene.scene.settings?.data?.spawnFacing;
      const carried = (typeof window !== 'undefined' && Number.isInteger(window.__myFacing)) ? window.__myFacing : null;
      const initial = Number.isInteger(spawnFacing) ? spawnFacing : carried;
      if (this._isLocal && Number.isInteger(initial)) this.setFacing8(initial);
      // Never show the flat front-facing art while the sheets download: a wrong-facing
      // flash on every reload is worse than a brief empty spot.
      this._animPending = true;
      this.layers.base.setVisible(false);
      // Next tick, not now: the scene calls setOutfit() right after construction, and the body sheet is then
      // fetched TOGETHER with the equipped items' sheets, so she does not appear undressed for a second.
      scene.time.delayedCall(0, () => { if (this.active) this._initWalkAnim(scene); });
    }

    // Globalny HUD tweakowy — jeden per scena, kontroluje pierwszą Natalkę. No on-screen 👗 button any
    // more (owner 2026-09-30: the outfit is chosen at the szafa in the house — Garderoba); the fit
    // editor still opens with the T key.
    if (!options.noTweakHud && !scene._clothTweakHud) {
      scene._clothTweakHud = new ClothTweakHud(scene, this);
    }

    // The local player's avatar shows its own Zzz when idle (default). Remote avatars
    // and the wardrobe preview pass trackIdle:false and are driven via setSleeping().
    this._sleeping = false;
    this._idle = false;
    this._zzz = null;
    // Sitting on a sofa / lying in a bed (setPose): the pose atlases' images replace the standing figure.
    this._pose = null;
    this._poseFlip = false;
    this._poseImgs = [];
    this._poseGen = 0;
    this._poseMissing = new Set();
    if (options.trackIdle ?? true) {
      this._idleUnsub = idleTracker.on((idle) => this.setSleeping(idle));
      if (idleTracker.idle) this.setSleeping(true);
    }
  }

  // The player went idle / came back: the local avatar hears it from idleTracker, remote ones from presence.
  // The rising "Zzz" shows while idle and, whatever the idle state, while lying in bed. Idempotent.
  setSleeping(on) {
    this._idle = !!on;
    this._syncZzz();
  }

  _syncZzz(rebuild = false) {
    const on = sleepShown({ idle: this._idle, lying: this._pose === 'lie' });
    if (on === this._sleeping && !rebuild) return;
    this._sleeping = on;
    if (on) this._buildZzz();
    else this._destroyZzz();
  }

  // Where the letters start, relative to the standing spot above the head. Posed, the container's origin is the
  // pose anchor (sitting: the feet; lying: the middle of the figure), so the head is somewhere else.
  _zzzShift() {
    const h = headTop(this._pose);
    return { x: (h.x - STANDING_HEAD_TOP.x) * (this._poseFlip ? -1 : 1), y: h.y - STANDING_HEAD_TOP.y };
  }

  _buildZzz() {
    this._destroyZzz();
    const shift = this._zzzShift();
    // Three letters of growing size, staggered, rising up-right past the head top (~-120).
    const defs = [
      { x: 26, y: -116, size: 16 },
      { x: 40, y: -128, size: 22 },
      { x: 56, y: -142, size: 30 },
    ].map((d) => ({ ...d, x: d.x + shift.x, y: d.y + shift.y }));
    this._zzz = defs.map((d, i) => {
      const t = this.scene.add.text(d.x, d.y, 'z', {
        fontFamily: 'system-ui', fontSize: `${d.size}px`, color: '#dff1ff', fontStyle: 'bold',
      }).setOrigin(0.5).setAlpha(0);
      t.setStroke('#244a6b', 4);
      this.add(t);
      const tweens = [
        this.scene.tweens.add({
          targets: t, y: d.y - 38, duration: 1500, delay: i * 450, repeat: -1,
          ease: 'Sine.easeOut', onRepeat: () => { t.y = d.y; },
        }),
        this.scene.tweens.add({
          targets: t, alpha: 1, duration: 750, delay: i * 450, yoyo: true, repeat: -1,
          ease: 'Sine.easeInOut',
        }),
      ];
      return { t, tweens };
    });
  }

  _destroyZzz() {
    if (!this._zzz) return;
    for (const { t, tweens } of this._zzz) { tweens.forEach((tw) => tw.remove()); t.destroy(); }
    this._zzz = null;
  }

  destroy(fromScene) {
    HAIR_SHEETS.release(this);
    this._idleUnsub?.();
    this._destroyZzz();
    super.destroy(fromScene);
  }

  // ---- PROTOTYPE animated walk -------------------------------------------------------
  _initWalkAnim(scene) {
    const bodyKey = WALK.body(this._character);
    const equipped = this._lastEquipped ?? {};
    const garmentKeys = Object.values(equipped).filter(Boolean).map((id) => this._itemSheet(id));
    const hair = hairKeys('walk', this._character, this._look, equipped);
    ensureWalkSheets(scene, [bodyKey, ...garmentKeys, hair.front, hair.back, ...(hair.neck ? [hair.neck] : [])], () => {
      if (!this.active) return;
      if (!scene.textures.exists(bodyKey)) {
        // No animated body for this character (yet): back to the flat art.
        this._animPending = false; this._animMissing.add(bodyKey);
        this.layers.base.setVisible(true); this.setOutfit(this._lastEquipped);
        return;
      }
      const d = WALK.display(NATALKA_W, NATALKA_H);
      const body = scene.add.sprite(0, d.dy, bodyKey, restFrame(scene, bodyKey, this._motion.row)).setDisplaySize(d.w, d.h);
      body.setFlipX(this._motion.flip);
      // Same slot as the flat body: right after accessory_back, before the garments.
      this.addAt(body, this.getIndex(this.layers.base) + 1);
      this.layers.base.setVisible(false);
      this._anim = { body, bodyKey, garments: {} };
      // The garments never run a walk of their own: they show the body's frame, every time it changes. Each
      // used to play its own copy, and one put on mid-step (in the Garderoba, or a friend changing clothes
      // as she walks) joined a frame off and stayed off — shoes beside the feet (owner 2026-10-03).
      body.on('animationupdate', () => this._syncGarments());
      this._animPending = false;
      this.setOutfit(this._lastEquipped);
      this._applyMotion();
      this._refreshLook();   // the body itself: a figure with no clothes on never reaches _applyAnimOutfit
    });
  }

  // Called by WalkController every frame with the screen-space move vector (0,0 = standing).
  setMotion(dx, dy) {
    const moving = dx !== 0 || dy !== 0;
    if (moving) this.setFacing8(dir8FromVector(dx, dy));
    this._motion.dx = dx; this._motion.dy = dy;
    this._applyMotion();
  }

  /** A display figure (the Wygląd preview) walks on the spot in the way it faces; false: it stands. */
  walkInPlace(on) { this._inPlace = !!on; this._applyMotion(); }

  /** ... or shows walk frame `col` (0-7) of its cycle, held still (a local-only frame stepper); null lets go. */
  holdWalkFrame(col) { this._held = col == null ? null : col; this._applyMotion(); }

  /** What the body shows: { pose: 'walk' | 'stand', row (WALK.rows), flip, frame (the sheet's), col (in the walk) }. */
  get shownFrame() {
    const sp = this._anim?.body;
    if (!sp) return null;
    const frame = Number(sp.frame.name), row = this._motion.row;
    const walking = sp.anims.isPlaying || this._held != null;
    return { pose: walking ? 'walk' : 'stand', row: WALK.rows[row], flip: sp.flipX, frame, col: frame % WALK.cols };
  }

  /** 8-way compass facing (E=0 clockwise); what peers and the DB see. */
  get facing8() { return this._motion.dir8; }

  setFacing8(dir8) {
    const d = rowFlipFromDir8(dir8);
    this._motion.dir8 = ((dir8 % 8) + 8) % 8; this._motion.row = d.row; this._motion.flip = d.flip;
    if (this._isLocal && typeof window !== 'undefined') window.__myFacing = this._motion.dir8;
    if (this._motion.dx === 0 && this._motion.dy === 0) this._applyMotion();
  }

  _applyMotion() {
    const a = this._anim;
    if (!a) return;
    const { row, flip, dx, dy } = this._motion;
    const moving = dx !== 0 || dy !== 0 || this._inPlace;
    const sp = a.body;
    sp.setFlipX(flip);
    const key = animKey(sp.texture.key, row);
    if (this._held != null) {
      if (sp.anims.isPlaying) sp.anims.stop();
      sp.setFrame(row * WALK.cols + this._held);
    } else if (moving) {
      if (sp.anims.currentAnim?.key !== key || !sp.anims.isPlaying) sp.play({ key, startFrame: WALK.startFrame });
    } else {
      const rest = restFrame(this.scene, sp.texture.key, row);
      if (sp.anims.isPlaying || sp.frame.name !== String(rest)) { sp.anims.stop(); sp.setFrame(rest); }
    }
    this._syncGarments();
    this._stackNeckItem();
    this._liftItem();
  }

  // The accessory over a style's own hair draws higher or lower by how that head differs from today's seen from this
  // side (hairArt ITEM_FIT: Buzz's head is 16 px lower from the front, 5 from behind).
  _liftItem() {
    const a = this._anim;
    const acc = a?.garments.accessory;
    if (!acc) return;
    const up = a.fitItem ? itemLift('walk', this._character, this._look, a.fitItem, ITEM_FIT, WALK.rows[this._motion.row]) : 0;
    const d = WALK.display(NATALKA_W, NATALKA_H);
    acc.setY(d.dy - up * d.h / WALK.frameH);
  }

  // Headphones round the neck go under hair that covers the back of the neck when she walks away (clothSlots
  // underTheHair): the accessory's walk sprite sits right below the front hair's in those rows, right above it otherwise.
  _stackNeckItem() {
    const acc = this._anim?.garments.accessory;
    const hair = this._anim?.garments.hair;
    if (!acc || !hair) return;
    if (underTheHair(this._lastEquipped?.akcesorium, WALK.rows[this._motion.row])) this.moveBelow(acc, hair);
    else this.moveAbove(acc, hair);
  }

  // Walking: every garment sheet has the body's grid, so a garment shows the body's frame number.
  // Standing: its own sheet's standing frame for the direction (restFrame — not every sheet has a stand row).
  // Facing right: a one-sided style's sheet has its own right-facing frames, drawn unflipped (rightRows.mjs).
  _syncGarments() {
    const a = this._anim;
    if (!a) return;
    const walking = a.body.anims.isPlaying || this._held != null;
    // a step drawn off its row moves back into line, every layer with the body (walkShift)
    const x = walking ? stepShift(this._character, a.body.frame.name, a.body.flipX) * WALK.display(NATALKA_W, NATALKA_H).w / WALK.frameW : 0;
    a.body.setX(x);
    for (const g of Object.values(a.garments)) {
      g?.setX(x);
      if (!g?.visible) continue;
      if (g.anims.isPlaying) g.anims.stop();
      const f = walking ? a.body.frame.name : restFrame(this.scene, g.texture.key, this._motion.row);
      const own = ownFrame((n) => g.texture.has(n), f, a.body.flipX);
      g.setFlipX(own.flip);
      if (String(g.frame.name) !== String(own.frame) && g.texture.has(own.frame)) g.setFrame(own.frame);
    }
  }

  // In anim mode the flat garment overlays cannot follow the pose: each equipped item shows
  // through its own generated sheet (the same grid as the body) on the layer of its category,
  // loaded on demand; an item without a sheet yet simply stays hidden.
  _applyAnimOutfit(equipped) {
    const a = this._anim;
    if (!a && !this._animPending) return;
    for (const key of ['bottom', 'top', 'jacket', 'shoes', 'accessory', 'accessory_back', 'hair', 'hair_back', 'neck']) this.layers[key]?.setVisible(false);
    if (!a) return;
    for (const [cat, layerName] of Object.entries(CATEGORY_TO_LAYER)) {
      const itemId = equipped?.[cat];
      const key = itemId ? this._itemSheet(itemId) : null;
      const sp = a.garments[layerName];
      if (!key || this._animMissing.has(key)) { sp?.setVisible(false); continue; }
      if (!this.scene.textures.exists(key)) {
        sp?.setVisible(false);
        ensureWalkSheets(this.scene, [key], () => {
          if (!this.scene.textures.exists(key)) {
            // Not there (yet): try again on a later outfit change — a sheet that was being
            // rewritten by the build script when we fetched it must not stay hidden until a reload.
            this._animMissing.add(key);
            setTimeout(() => this._animMissing.delete(key), 10000);
          }
          if (this.active && this._lastEquipped === equipped) this._applyAnimOutfit(equipped);
        });
        continue;
      }
      this._walkLayer(layerName, key).setVisible(true);
    }
    // The hair: two sprites like the garments ('hair_back' behind the body, 'hair' over the clothes), following the
    // body's frame, and a third under the clothes: today's 'neck' with the default style (decision 15), the style's own
    // ears with any other when walking (hairArt names). A style whose
    // sheets did not load draws the default style's; the old hair stays until they land, and when not even the
    // default's load she is the flat art again (its hair painted in), never a bald walk.
    let hair = hairKeys('walk', this._character, this._look, equipped);
    if (this._animMissing.has(hair.front)) hair = hairFallback(this._character);
    const tex = this.scene.textures;
    if (tex.exists(hair.front) && tex.exists(hair.back) && (!hair.neck || tex.exists(hair.neck))) {
      this._walkLayer('hair', hair.front).setVisible(true);
      this._walkLayer('hair_back', hair.back).setVisible(true);
      if (hair.neck) this._walkLayer('neck', hair.neck).setVisible(true);
      else a.garments.neck?.setVisible(false);
      // an item painted on today's hair that sits too low or high on this style draws where it fits (_liftItem)
      a.fitItem = hair.front === hairFallback(this._character).front ? null : equipped?.akcesorium;
      HAIR_SHEETS.use(this, [hair.front, hair.back, hair.neck].filter(Boolean));   // a phone frees the sheets nobody draws
    } else {
      ensureHairSheets(this.scene, hair, this._character, (got) => {
        if (!this.active || this._anim !== a) return;
        if (got === null) { this._dropAnim(); return; }
        if (got !== hair) { this._animMissing.add(hair.front); setTimeout(() => this._animMissing.delete(hair.front), 10000); }
        if (this.active && this._lastEquipped === equipped) this._applyAnimOutfit(equipped);
      });
    }
    this._applyMotion();
    if (this._seated) this._applySeatCrop();
    if (this._pose) this._hideStanding();
    this._refreshLook();
  }

  // Back to the flat art, as for a body sheet that does not load: the walk sprites go, the flat body and clothes show.
  _dropAnim() {
    const a = this._anim;
    this._anim = null;
    this._animPending = false;
    this._animMissing.add(a.bodyKey);
    a.body.destroy();
    Object.values(a.garments).forEach((g) => g?.destroy());
    if (!this._pose) this.layers.base.setVisible(true);
    this.setOutfit(this._lastEquipped);
  }

  // An item's walk sheet: the copy cut for the hairstyle's tail where it hangs over the item (hairArt ITEM_CUT), its own
  // when that copy did not load.
  _itemSheet(itemId) {
    const own = WALK.garment(this._character, itemId);
    const cut = cutSheet(own, this._character, this._look, itemId);
    return this._animMissing.has(cut) ? own : cut;
  }

  // The walk sprite of a garment or hair layer, made on first use right above that layer's flat anchor image.
  _walkLayer(layerName, key) {
    const a = this._anim;
    let sp = a.garments[layerName];
    if (!sp) {
      const d = WALK.display(NATALKA_W, NATALKA_H);
      sp = this.scene.add.sprite(0, d.dy, key, 0).setDisplaySize(d.w, d.h);
      this.addAt(sp, this.getIndex(this.layers[layerName]) + 1);
      a.garments[layerName] = sp;
    } else if (sp.texture.key !== key) {
      sp.anims.stop(); sp.setTexture(key, 0);
    }
    return sp;
  }

  // Whether the avatar is currently in the cropped/seated pose. Read by ChatBubble (and
  // anything else that needs to offset relative to the visible head) instead of poking
  // _seated directly. Non-NatalkaSprite chat targets simply lack this getter, so
  // `target.seated` reads as undefined for them — safe by default.
  get seated() { return !!this._seated; }

  /** How big this character is in her scene's world — 1 on a flat room, less outdoors / towards the back wall. */
  get worldScale() { return this._worldScale; }

  /** Where her feet are (the art's feet sit 120 px below the centre at scale 1); posed, the pose anchor is the spot. */
  get feetY() { return this._pose ? this.y : this.y + (NATALKA_H / 2) * this._worldScale; }

  /** 'sit' | 'lie' | null (standing). */
  get pose() { return this._pose; }

  /** Which character's body this is ('natalka' | 'ken'), and what she has on (the last setOutfit). */
  get character() { return this._character; }
  get equipped() { return this._lastEquipped; }

  // Sit on a sofa / lie in a bed, or stand up again (null). Posed, the container's (x, y) is the pose anchor on the
  // piece (furnitureSeatsLogic spotsFor) and `flip` mirrors the figure for a flipped piece. The walk body and its
  // garments hide; the pose images show the moment their atlases are in (nothing until then — never the standing
  // figure floating on the sofa).
  setPose(pose, { flip = false } = {}) {
    const next = pose === 'sit' || pose === 'lie' ? pose : null;
    if (next === this._pose && !!flip === this._poseFlip) return;
    const was = this._pose;
    this._pose = next;
    this._poseFlip = !!flip;
    if (!next && was) this._showStanding();
    this._refreshPose();
    this._syncZzz(true);
  }

  _hideStanding() {
    for (const sp of Object.values(this.layers)) sp.setVisible(false);
    if (this._anim) { this._anim.body.setVisible(false); Object.values(this._anim.garments).forEach((g) => g?.setVisible(false)); }
  }

  _showStanding() {
    if (this._anim) this._anim.body.setVisible(true);
    else if (!this._animPending) this.layers.base.setVisible(true);
    // setOutfit puts the garments back (and calls _refreshPose, which clears the pose images).
    this.setOutfit(this._lastEquipped);
  }

  // (Re)draw the pose for the current outfit: what is loaded now, then again once the atlases it needs are in.
  _refreshPose() {
    const gen = ++this._poseGen;
    if (!this._pose) { this._clearPoseImgs(); return; }
    this._hideStanding();
    // What is loaded shows at once (the body without a garment still on its way), the rest as it lands: never a blank
    // on a slow phone.
    this._drawPose();
    const keys = poseKeys(this._character, this._lastEquipped, this._look).filter((k) => !this._poseMissing.has(k));
    ensurePoseSheets(this.scene, keys, () => {
      if (!this.active || gen !== this._poseGen) return;
      for (const k of keys) {
        if (this.scene.textures.exists(k)) continue;
        // Not there (an item with no pose layer yet, or a failed download): ask again on a later change.
        this._poseMissing.add(k);
        setTimeout(() => this._poseMissing.delete(k), 10000);
      }
      this._drawPose();
    });
  }

  _clearPoseImgs() {
    for (const img of this._poseImgs) img.destroy();
    this._poseImgs = [];
  }

  _drawPose() {
    this._clearPoseImgs();
    const tex = this.scene.textures;
    const layers = poseLayers({
      char: this._character, pose: this._pose, equipped: this._lastEquipped, look: this._look,
      has: (key, frame) => tex.exists(key) && tex.get(key).has(frame), flip: this._poseFlip,
    });
    const a = POSE.anchor[this._pose];
    layers.forEach(({ key, frame }, i) => {
      const img = this.scene.add.image(0, 0, key, frame)
        .setOrigin(a.x / POSE.cell.w, a.y / POSE.cell.h).setScale(POSE_SCALE).setFlipX(this._poseFlip);
      this.addAt(img, i);
      this._poseImgs.push(img);
    });
    this._refreshLook();
  }

  /** The player's look (null = the art as drawn). Masks load only for a look that needs them. */
  setLook(look) {
    this._look = look ?? null;
    if (this._anim) this._applyAnimOutfit(this._lastEquipped);   // another hairstyle is other hair sheets
    if (this._pose) this._refreshPose();
    this._refreshLook();
  }

  get look() { return this._look; }

  // The pictures that carry a mask: the walk body and garments, and the sofa / bed images.
  _lookSprites() {
    const out = [];
    if (this._anim) out.push(this._anim.body, ...Object.values(this._anim.garments).filter(Boolean));
    out.push(...this._poseImgs);
    return out;
  }

  _refreshLook() {
    const paint = () => { if (this.active) for (const sp of this._lookSprites()) applyLook(sp, this._character, this._look); };
    if (isDefaultLook(this._character, this._look)) { paint(); return; }
    ensureMasks(this.scene, [...new Set(this._lookSprites().map((sp) => sp.texture.key))], paint);
  }

  setWorldScale(s) {
    this._worldScale = Number.isFinite(s) && s > 0 ? s : 1;
    this._applyScale();
  }

  _applyScale() { this.setScale((this._seated ? SEAT_SCALE : 1) * this._worldScale); }

  // Seated look on/off — scales the whole container down and crops every layer to its
  // top SEAT_CROP_FRAC (head + chest only). See the geometry note by SEAT_SCALE.
  setSeated(on) {
    this._seated = !!on;
    this._applyScale();
    this._applySeatCrop();
  }

  _applySeatCrop() {
    const sprites = ALL_LAYERS.map((k) => this.layers[k]);
    if (this._anim) sprites.push(this._anim.body, ...Object.values(this._anim.garments));
    for (const sp of sprites) {
      if (!sp) continue;
      if (this._seated) sp.setCrop(0, 0, sp.width, sp.height * SEAT_CROP_FRAC);
      else sp.setCrop();
    }
  }

  setOutfit(equipped) {
    this._lastEquipped = equipped;
    if (!equipped) { this._applyAnimOutfit(null); this._refreshPose(); return; }   // nothing on, the hair still is
    for (const [cat, layerName] of Object.entries(CATEGORY_TO_LAYER)) {
      const itemId = equipped[cat];
      const sp = this.layers[layerName];
      // Ken's clothes only exist as walk sheets: their square shop picture is not body-shaped art.
      const key = itemId && !sheetOnly(itemId) ? `cloth_${itemId}` : null;
      if (!key) { sp.setVisible(false); continue; }
      if (!this.scene.textures.exists(key)) {
        sp.setVisible(false);
        // Someone else's outfit we have not downloaded yet: draw it the moment it lands.
        preloader.whenReady(key, () => { if (this.active && this._lastEquipped === equipped) this.setOutfit(equipped); });
        continue;
      }
      sp.setTexture(key).setVisible(true);
      this._applyTweakToSprite(sp, key);
    }

    // Accessory part that wraps BEHIND the head (e.g. a crown's rim). Drawn on the
    // accessory_back layer with the SAME tweak as the front part so the two halves line
    // up into one item. Only shows when the equipped accessory ships a `_back` texture.
    const accId = equipped.akcesorium;
    const backSp = this.layers.accessory_back;
    const accFrontKey = accId ? `cloth_${accId}` : null;
    const accBackKey = accId ? `cloth_${accId}_back` : null;
    if (accBackKey && this.scene.textures.exists(accBackKey)) {
      backSp.setTexture(accBackKey).setVisible(true);
      this._applyTweakToSprite(backSp, accFrontKey);
    } else {
      backSp.setVisible(false);
      // Only wait on it if it's a real asset (most accessories have no back part at all —
      // the manifest is the oracle for that, same as assetManifest.mjs's own `exists()`).
      if (accBackKey && accBackKey in ASSETS) {
        preloader.whenReady(accBackKey, () => { if (this.active && this._lastEquipped === equipped) this.setOutfit(equipped); });
      }
    }

    // Textures just changed → Phaser's crop rect is per-texture, so re-apply it.
    if (this._seated) this._applySeatCrop();
    this._applyAnimOutfit(equipped);
    this._refreshPose();
  }

  // Re-render after tweak change (without full setOutfit)
  refresh() { this.setOutfit(this._lastEquipped); }

  // Keep the behind-the-head accessory part aligned with the front part while the
  // 👗 editor tweaks the 'accessory' layer (which only touches the front sprite).
  _syncAccessoryBack(frontKey) {
    const back = this.layers.accessory_back;
    if (back && back.visible) this._applyTweakToSprite(back, frontKey);
  }

  _applyTweakToSprite(sp, clothKey) {
    const tw = this._tweakSource.get(clothKey);
    sp.setDisplaySize(NATALKA_W * tw.scaleW, NATALKA_H * tw.scaleH)
      .setPosition(tw.offsetX, tw.offsetY);
  }

  // Zwróć info o aktualnej warstwie (klucz tekstury + tweak)
  getLayerInfo(layerName) {
    const sp = this.layers[layerName];
    if (!sp || !sp.visible) return { key: null, tweak: null };
    const key = sp.texture.key;
    if (!key.startsWith('cloth_')) return { key: null, tweak: null };
    return { key, tweak: clothTweaks.get(key) };
  }

  // Zastosuj częściowy tweak do warstwy + zapisz w localStorage
  applyLayerTweak(layerName, partial) {
    const sp = this.layers[layerName];
    if (!sp || !sp.visible) return;
    const key = sp.texture.key;
    if (!key.startsWith('cloth_')) return;
    clothTweaks.set(key, partial);
    this._applyTweakToSprite(sp, key);
    if (layerName === 'accessory') this._syncAccessoryBack(key);
  }

  resetLayerTweak(layerName) {
    const sp = this.layers[layerName];
    if (!sp || !sp.visible) return;
    const key = sp.texture.key;
    if (!key.startsWith('cloth_')) return;
    clothTweaks.reset(key);
    this._applyTweakToSprite(sp, key);
    if (layerName === 'accessory') this._syncAccessoryBack(key);
  }
}
